import { useState } from "react";
import { useEvidence } from "@/hooks/use-mock-data";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

interface EvidencePanelProps {
  opportunityId: string;
}

export function EvidencePanel({ opportunityId }: EvidencePanelProps) {
  const { data: evidence, isLoading } = useEvidence(opportunityId);
  const [expanded, setExpanded] = useState(false);

  if (isLoading) return null;
  if (evidence.data.length === 0) return null;

  // Group by classification
  const groups: Record<string, typeof evidence.data> = {};
  for (const signal of evidence.data) {
    const key = signal.classification || "unknown";
    if (!groups[key]) groups[key] = [];
    groups[key]!.push(signal);
  }

  const groupLabels: Record<string, string> = {
    pain_signal: "Pain Signals",
    builder_intel: "Builder Intel",
    trend_signal: "Trend Signals",
    unknown: "Other",
  };

  return (
    <div className="mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-3 group"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        )}
        <h2 className="font-brand text-lg tracking-wide group-hover:text-electric-blue transition-colors">
          EVIDENCE ({evidence.data.length} signals)
        </h2>
      </button>

      {expanded && (
        <div className="space-y-4">
          {Object.entries(groups).map(([key, signals]) => (
            <div key={key}>
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold block mb-2">
                {groupLabels[key] || key}
              </span>
              <div className="space-y-1.5">
                {signals.map((signal) => (
                  <div
                    key={signal.id}
                    className="glass-card rounded-lg p-3 flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-foreground leading-tight mb-1">
                        {signal.title}
                      </p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span className="bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 rounded">
                          {signal.sourcePlatform}
                        </span>
                        {signal.community && <span>{signal.community}</span>}
                        <span className="font-mono">{signal.engagementScore} eng.</span>
                      </div>
                    </div>
                    {signal.url && (
                      <a
                        href={signal.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-electric-blue shrink-0 mt-0.5"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
